import clsx from 'clsx'
import React, { useState } from 'react'

type Props = {
    children: React.ReactNode | React.ReactNode[]
    footer?: React.ReactNode | React.ReactNode[]
    classes?: string
    showTopSection?: boolean
    onClick?: () => void
    onSelectChanged?: (selected: boolean) => void
    topSectionClassNames?: string
}

function Card({
    children,
    footer,
    classes,
    showTopSection = true,
    onClick,
    onSelectChanged,
    topSectionClassNames,
}: Props): React.JSX.Element {
    const [selected, setSelected] = useState(false)

    if (onClick && onSelectChanged) {
        throw new Error(
            'Card component cannot have both onClick and onSelectChanged props',
        )
    }

    // Base styles with conditional styling for selected state.
    let allClasses = clsx(
        'border rounded-lg p-4 flex flex-col justify-between relative',
        classes,
        {
            // Gray out the card and update the border when selected.
            'bg-gray-300 border-gray-400': selected,
            'border-gray-300': !selected,
        },
    )

    const topSectionClasses = clsx(
        'absolute top-0 left-0 w-full h-[90px] bg-gray-200 rounded-t-lg',
        topSectionClassNames,
    )

    if (onClick || onSelectChanged) {
        allClasses = clsx(
            allClasses,
            'cursor-pointer',
            // Only add the blue outline on hover if the card is not selected.
            !selected &&
                'hover:outline hover:outline-2 hover:outline-ctxt-blue',
        )
    }

    function handleOnClick() {
        if (onClick) {
            onClick()
            return
        }

        if (onSelectChanged) {
            const newSelected = !selected
            setSelected(newSelected)
            onSelectChanged(newSelected)
        }
    }

    return (
        <div className={allClasses} onClick={handleOnClick}>
            {/* Check mark icon if selected */}
            {selected && (
                <div className="absolute top-2 right-2 z-20">
                    <svg
                        className="w-6 h-6 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                        />
                    </svg>
                </div>
            )}

            {/* Gray Top Section */}
            {showTopSection && <div className={topSectionClasses}></div>}

            {/* Content Section */}
            <div className="overflow-hidden flex-1 z-10">{children}</div>

            {/* Footer Section */}
            <div className="flex">{footer}</div>
        </div>
    )
}

export default Card
